import React, { useState, useEffect } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import "../../../../../css/CashflowModeller2/Cards/DialogBoxCards/DrawdownDialogBox.css";
import Close from "../Buttons/Close";
import Input2 from "../Inputs/Input2";
import Input1 from "../Inputs/Input1";
import InputMedium from "../Inputs/InputMedium";
import Input2Medium from "../Inputs/Input2Medium";
import InputSmall from "../Inputs/InputSmall";
import AddButton from "../Buttons/AddButton";
import AddIcon from "../Buttons/AddIcon";
import RemoveIcon from "../Buttons/RemoveIcon";
import RemoveButton from "../Buttons/RemoveButton";
import { useCfmContext } from "../../Context/CfmContext";
import ATROptions from "../../Utils/ATROptions";
import useNameOptions from "../../Utils/NameOptions";
import TimelineOptions from "../../Utils/TimelineOptions";
import DrawdownOptions from "../../Utils/DrawdownOptions";
import {
  validateDrawdownFields,
  validatePensionRow,
} from "./Utils/ValidatePension";
import useDraggableDialog from "./Utils/UseDraggableDialog";
import Input1Big from "../Inputs/Input1Big";

const defaultRow = {
  name: "",
  type: "",
  fundValue: { value: "", unit: TimelineOptions[1].value },
  contribution: { value: "", unit: TimelineOptions[1].value },
  atr: "",
  drawdowns: [],
};

/**
 * PensionDialogBox component for managing pension entries and their drawdowns.
 *
 * This dialog allows users to add, edit, and remove pension records, each with associated drawdown periods.
 * It supports validation for unique pension names, valid age ranges, and overlapping drawdown periods.
 * The dialog is draggable and interacts with a global context for state management.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onClose - Callback function to close the dialog.
 *
 * @returns {JSX.Element} The PensionDialogBox component.
 *
 * @example
 * <PensionDialogBox onClose={() => setShowDialog(false)} />
 *
 * @remarks
 * - Uses context for pension state management.
 * - Validates drawdown ranges and pension uniqueness.
 * - Supports editing of existing pensions and their drawdowns.
 * - Provides UI feedback for validation errors.
 */
const PensionDialogBox = ({ onClose }) => {
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [drawdowns, setDrawdowns] = useState([]);
  const [drawdownInput, setDrawdownInput] = useState({
    value: "",
    unit: TimelineOptions[1].value,
    start: "",
    end: "",
  });
  const [inputErrors, setInputErrors] = useState({
    value: false,
    start: false,
    end: false,
  });
  const [pensionDrawdownErrors, setPensionDrawdownErrors] = useState({});
  const [pensionDrawdownInputs, setPensionDrawdownInputs] = useState({});
  const [pensionInputErrors, setPensionInputErrors] = useState({
    duplicateName: false,
  });
  const { state, dispatch } = useCfmContext();
  const pensions = state.pensions || [];
  const nameOptions = useNameOptions();
  const { ref, position, handleMouseDown } = useDraggableDialog();

  /**
   * Handles changes to input fields by updating the corresponding field in the inputRow state.
   *
   * @param {string} field - The name of the field to update.
   * @param {*} value - The new value to set for the specified field.
   */
  const handleInputChange = (field, value) => {
    setInputRow((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Checks if a new range (newStart to newEnd) overlaps with any existing ranges.
   *
   * @param {Array<{start: number|string, end: number|string}>} existing - Array of existing ranges, each with a start and end property.
   * @param {number} newStart - The start value of the new range.
   * @param {number} newEnd - The end value of the new range.
   * @returns {boolean} Returns true if the new range overlaps with any existing range, otherwise false.
   */
  const isRangeOverlapping = (existing, newStart, newEnd) =>
    existing.some(({ start, end }) => {
      const s = parseInt(start, 10);
      const e = parseInt(end, 10);
      return !(newEnd < s || newStart > e);
    });

  /**
   * Checks if a pension name is unique for a given person within the pensions list.
   *
   * @param {string} personName - The name of the person to check against.
   * @param {string} pensionName - The name of the pension to check for uniqueness.
   * @returns {boolean} Returns true if the pension name is unique for the specified person, otherwise false.
   */
  const isPensionNameUnique = (personName, pensionName) => {
    return !pensions.some(
      (p) =>
        p.name?.trim().toLowerCase() === personName?.trim().toLowerCase() &&
        p.type?.trim().toLowerCase() === pensionName?.trim().toLowerCase()
    );
  };
  // Count how many drawdown input errors are true
  // const activeErrors = [
  //   inputErrors.value,
  //   inputErrors.start,
  //   inputErrors.end,
  //   inputErrors.rangeError,
  // ].filter(Boolean).length;
  const [touched, setTouched] = useState(false);

  /**
   * Handles the addition of a new drawdown entry.
   *
   * Validates the drawdown input fields for value, start, and end ages.
   * Checks for the following errors:
   * - Empty value, start, or end fields
   * - Start or end age less than 18
   * - End age less than start age
   * - Overlapping age ranges with existing drawdowns
   *
   * If validation fails, sets appropriate error states and prevents addition.
   * If validation passes, adds the new drawdown to the list, resets input fields and errors.
   *
   * @function
   * @returns {void}
   */
  const handleAddDrawdown = () => {
    setTouched(true);

    const valueError = drawdownInput.value?.trim() === "";
    const startError = drawdownInput.start?.trim() === "";
    const endError = drawdownInput.end?.trim() === "";

    const startNum = parseInt(drawdownInput.start, 10);
    const endNum = parseInt(drawdownInput.end, 10);

    const ageTooYoungStart = !isNaN(startNum) && startNum < 18;
    const ageTooYoungEnd = !isNaN(endNum) && endNum < 18;
    const rangeInvalid =
      !isNaN(startNum) && !isNaN(endNum) && endNum < startNum;

    const hasOverlap = isRangeOverlapping(drawdowns, startNum, endNum);

    const errors = {
      value: valueError,
      start: startError || ageTooYoungStart || rangeInvalid || hasOverlap,
      end: endError || ageTooYoungEnd || rangeInvalid || hasOverlap,
      rangeError: rangeInvalid || hasOverlap,
    };

    const hasErrors = errors.value || errors.start || errors.end;

    if (hasErrors) {
      setInputErrors(errors);
      return;
    }

    // Valid — add it
    setDrawdowns((prev) => [...prev, drawdownInput]);
    setDrawdownInput({
      value: "",
      unit: TimelineOptions[1].value,
      start: "",
      end: "",
    });
    setInputErrors({
      value: false,
      start: false,
      end: false,
      rangeError: false,
    });
    setTouched(false);
  };

  /**
   * Handles the addition of a new pension entry with drawdown details.
   *
   * This function performs the following steps:
   * 1. Marks the form as touched to trigger validation.
   * 2. Checks if any drawdown input fields are present.
   * 3. Validates drawdown input fields for presence, range errors, and overlapping periods.
   * 4. Sets input errors if validation fails and exits early.
   * 5. Validates the entire pension row and all drawdowns for correctness and uniqueness.
   * 6. Sets pension input errors if validation fails and exits early.
   * 7. If all validations pass, adds the new drawdown to the list.
   * 8. Dispatches an action to update the pensions state with the new entry.
   * 9. Resets input fields and error states to their default values.
   *
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    setTouched(true);

    const { value, start, end } = drawdownInput;
    const isBufferPresent = [value, start, end].some((v) => v.trim() !== "");

    let drawdownErrors = {
      value: false,
      start: false,
      end: false,
      rangeError: false,
    };
    let combinedDrawdowns = [...drawdowns];

    if (isBufferPresent) {
      const startNum = parseInt(start, 10);
      const endNum = parseInt(end, 10);
      const hasRangeError =
        !isNaN(startNum) && !isNaN(endNum) && endNum < startNum;
      const hasOverlap = isRangeOverlapping(drawdowns, startNum, endNum);

      drawdownErrors = {
        value: value.trim() === "",
        start: start.trim() === "" || hasRangeError || hasOverlap,
        end: end.trim() === "" || hasRangeError || hasOverlap,
        rangeError: hasRangeError || hasOverlap, // THIS!
      };

      if (Object.values(drawdownErrors).some(Boolean)) {
        setInputErrors(drawdownErrors);
        return;
      }
    }

    const isValidPension = validatePensionRow({
      ...inputRow,
      drawdowns: [
        ...combinedDrawdowns,
        ...(isBufferPresent ? [drawdownInput] : []),
      ],
    });

    const areAllDrawdownsValid = [
      ...combinedDrawdowns,
      ...(isBufferPresent ? [drawdownInput] : []),
    ].every(validateDrawdownFields);
    const isUnique = isPensionNameUnique(inputRow.name.trim(), inputRow.type.trim());

    if (!isValidPension || !areAllDrawdownsValid || !isUnique) {
      setPensionInputErrors({ duplicateName: !isUnique });
      return;
    }

    if (isBufferPresent) {
      combinedDrawdowns.push(drawdownInput);
    }

    dispatch({
      type: "UPDATE_PENSIONS",
      payload: [...pensions, { ...inputRow, drawdowns: combinedDrawdowns }],
    });

    setInputRow({ ...defaultRow });
    setDrawdowns([]);
    setDrawdownInput({
      value: "",
      unit: TimelineOptions[1].value,
      start: "",
      end: "",
    });
    setInputErrors({
      value: false,
      start: false,
      end: false,
      rangeError: false,
    });
    setPensionInputErrors({ duplicateName: false });
  };

  /**
   * Removes a pension entry at the specified index from the pensions list.
   *
   * Dispatches an action to update the pensions state by filtering out
   * the pension at the given index.
   *
   * @param {number} index - The index of the pension to remove.
   */
  const handleRemovePension = (index) => {
    dispatch({
      type: "UPDATE_PENSIONS",
      payload: pensions.filter((_, i) => i !== index),
    });
  };

  /**
   * Removes a drawdown entry at the specified index from the drawdowns list.
   *
   * @param {number} index - The index of the drawdown entry to remove.
   * @returns {void}
   */
  const handleRemoveDrawdown = (index) => {
    setDrawdowns((prev) => prev.filter((_, i) => i !== index));
  };

  const isPensionValid = validatePensionRow({
    ...inputRow,
    drawdowns: drawdowns.length ? drawdowns : [],
  });

  const areDrawdownsValid = drawdowns.every(validateDrawdownFields);

  /**
   * Handles changes to the pension drawdown input fields.
   * Updates both the input values and resets any associated errors for the specified field and index.
   *
   * @param {number|string} index - The index or key identifying the pension drawdown input to update.
   * @param {string} field - The name of the field within the pension drawdown input to update.
   * @param {*} value - The new value to set for the specified field.
   */
  const handlePensionDrawdownInputChange = (index, field, value) => {
    setPensionDrawdownInputs((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: value },
    }));

    setPensionDrawdownErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: false },
    }));
  };

  /**
   * Handles the addition of a pension drawdown entry for a specific pension index.
   *
   * Validates the input fields (value, start, end) for completeness and logical correctness,
   * checks for overlapping drawdown periods, and updates the pension drawdowns if valid.
   * If there are validation errors or overlapping periods, sets the appropriate error states.
   *
   * @param {number} index - The index of the pension in the pensions array to which the drawdown should be added.
   *
   * Side Effects:
   * - Updates pension drawdown errors state if validation fails.
   * - Updates pensions state with the new drawdown entry if validation passes.
   * - Resets the drawdown input fields for the given index after successful addition.
   * - Dispatches an action to update the pensions in the global state.
   */
  const handleAddPensionDrawdown = (index) => {
    const input = pensionDrawdownInputs[index] || {};
    const start = parseInt(input.start, 10);
    const end = parseInt(input.end, 10);

    const errors = {
      value: !input.value?.trim(),
      start:
        !input.start?.trim() || (!isNaN(start) && !isNaN(end) && end < start),
      end: !input.end?.trim() || (!isNaN(start) && !isNaN(end) && end < start),
    };

    const hasOverlap = (pensions[index]?.drawdowns || []).some(
      ({ start: s, end: e }) => {
        const startNum = parseInt(s, 10);
        const endNum = parseInt(e, 10);
        return !(end < startNum || start > endNum);
      }
    );

    const hasErrors = Object.values(errors).some(Boolean) || hasOverlap;

    // console.log(hasOverlap, "hasOverlap");

    if (hasErrors) {
      setPensionDrawdownErrors((prev) => ({
        ...prev,
        [index]: {
          ...errors,
          rangeError: hasOverlap,
        },
      }));
      // console.log("Pension drawdown input errors:", {
      //   ...errors,
      //   rangeError: hasOverlap,
      // });
      return;
    }

    const updatedPensions = [...pensions];
    const existing = updatedPensions[index].drawdowns || [];
    const drawdownEntry = {
      value: input.value,
      start: input.start,
      end: input.end,
      unit: input.unit || "Per annum", // fallback just in case
    };

    updatedPensions[index].drawdowns = [...existing, drawdownEntry];

    dispatch({ type: "UPDATE_PENSIONS", payload: updatedPensions });

    // Reset the drawdown input for that index
    setPensionDrawdownInputs((prev) => ({
      ...prev,
      [index]: {
        value: "",
        start: "",
        end: "",
        unit: "Per annum",
      },
    }));
    setPensionDrawdownErrors((prev) => ({
      ...prev,
      [index]: { value: false, start: false, end: false },
    }));
  };

  const [tempTypes, setTempTypes] = useState(pensions.map((p) => p.type));

  useEffect(() => {
    setTempTypes(pensions.map((p) => p.type || ""));
  }, [pensions]);

  // const areDrawdownsValid = drawdowns.every(validateDrawdownFields);

  // Use if-else block to compute exactly ONE error message:
  let drawdownErrorMessage = "";
  if (touched) {
    const activeErrors = [
      inputErrors.value,
      inputErrors.start,
      inputErrors.end,
      inputErrors.rangeError,
    ].filter(Boolean).length;

    if (inputErrors.rangeError) {
      drawdownErrorMessage = "Invalid age range.";
    }
    // else if (inputErrors.value) {
    //   drawdownErrorMessage = "Drawdown value is required.";
    // }
    // else if (inputErrors.start) {
    //   drawdownErrorMessage =
    //     "Start age must be at least 18 and less than End age.";
    // } else if (inputErrors.end) {
    //   drawdownErrorMessage =
    //     "End age must be at least 18 and greater than Start age.";
    // }
  }

  /**
   * Handles changes to a pension field, including nested fields, and dispatches an update action.
   *
   * @param {number} index - The index of the pension item to update in the pensions array.
   * @param {string} field - The field of the pension item to update.
   * @param {*} value - The new value to set for the specified field.
   * @param {string} [nestedField] - Optional. The nested field within the main field to update, if applicable.
   */
  const handlePensionFieldChange = (index, field, value, nestedField) => {
    const updatedPensions = [...pensions];
    if (nestedField) {
      updatedPensions[index][field] = {
        ...updatedPensions[index][field],
        [nestedField]: value,
      };
    } else {
      updatedPensions[index][field] = value;
    }
    dispatch({
      type: "UPDATE_PENSIONS",
      payload: updatedPensions,
    });
  };

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Pension</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>
      <div className="dialog-row">
        <div className="drawdown-column-container">
          <div className="dialog-row-inputs">
            <Input2Medium
              label="Name"
              value={inputRow.name?.split(" ")[0] || ""}
              onChange={(val) => handleInputChange("name", val)}
              options={nameOptions}
              type="invType"
              // readOnly
            />
            <Input1Big
              label="Pension Name"
              value={inputRow.type}
              onChange={(val) => {
                handleInputChange("type", val);
                setPensionInputErrors({ duplicateName: false });
              }}
            />
            <Input1Big
              label="Fund Value"
              value={inputRow.fundValue.value}
              onChange={(val) =>
                handleInputChange("fundValue", {
                  ...inputRow.fundValue,
                  value: val,
                })
              }
              onUnitChange={(unit) =>
                handleInputChange("fundValue", {
                  ...inputRow.fundValue,
                  unit,
                })
              }
              type="number"
              currency={true} // Assuming fund value is in currency
            />
            <Input2Medium
              label="Contribution"
              type="number"
              value={inputRow.contribution.value}
              onChange={(val) =>
                handleInputChange("contribution", {
                  ...inputRow.contribution,
                  value: val,
                })
              }
              onUnitChange={(unit) =>
                handleInputChange("contribution", {
                  ...inputRow.contribution,
                  unit,
                })
              }
              options={TimelineOptions}
              currency={true} // Assuming contribution is in currency
            />
            <Input2Medium
              label="Growth Rate"
              type="per"
              value={inputRow.atr ? `${inputRow.atr}%` : ""}
              onChange={(val) => {
                // console.log("ATR value changed:", val);
                handleInputChange("atr", val);
              }}
              options={ATROptions}
              per={true}
              currency={false}
            />
          </div>
          {pensionInputErrors.duplicateName && (
            <div className="error Label right-align">
              Pension name already exists. Please use a unique name.
            </div>
          )}
        </div>

        <div className="drawdown-column-container">
          <div className="card-rows no-label-container">
            <Input2Medium
              label="Drawdown"
              value={drawdownInput.value}
              onChange={(val) => {
                setDrawdownInput((prev) => ({
                  ...prev,
                  value: val,
                }));
                setInputErrors((prev) => ({
                  ...prev,
                  value: false,
                  rangeError: false,
                }));
                setTouched(false);
              }}
              onUnitChange={(unit) =>
                setDrawdownInput((prev) => ({
                  ...prev,
                  unit, // unit is the selected option's value: "Percentage" or "Annual"
                }))
              }
              options={DrawdownOptions}
              isError={inputErrors.value}
              type="number"
              currency={
                drawdownInput.unit === "Per annum" ||
                drawdownInput.unit === "Annual"
              } // Assuming drawdown value is in currency
              per={drawdownInput.unit === "Percentage %"} // Assuming drawdown value can be percentage
            />

            <InputSmall
              label="Start"
              value={drawdownInput.start}
              onChange={(val) => {
                setDrawdownInput((prev) => ({ ...prev, start: val }));
                if (val.trim() !== "") {
                  setInputErrors((prev) => ({
                    ...prev,
                    start: false,
                    end: false,

                    rangeError: false,
                  }));
                }
                setTouched(false);
              }}
              isError={inputErrors.start}
            />

            <InputSmall
              label="End"
              value={drawdownInput.end}
              onChange={(val) => {
                setDrawdownInput((prev) => ({ ...prev, end: val }));
                if (val.trim() !== "") {
                  setInputErrors((prev) => ({
                    ...prev,
                    end: false,
                    start: false,
                    rangeError: false,
                  }));
                }
                setTouched(false);
              }}
              isError={inputErrors.end}
            />

            <AddIcon onClick={handleAddDrawdown} />
          </div>
          <div className="error Label">{drawdownErrorMessage}</div>

          {drawdowns.map((dd, i) => (
            <div className="card-rows no-label-container drawdown" key={i}>
              <Input2Medium
                value={dd.value}
                onChange={(val) => {
                  const updated = [...drawdowns];
                  updated[i].value = val;
                  setDrawdowns(updated);
                }}
                onUnitChange={(unit) => {
                  const updated = [...drawdowns];
                  updated[i].unit = unit;
                  setDrawdowns(updated);
                }}
                options={DrawdownOptions}
                isError={inputErrors.value}
                type="number"
                currency={dd.unit === "Per annum" || dd.unit === "Annual"} // Assuming drawdown value is in currency
                per={dd.unit === "Percentage %"} // Assuming drawdown value can be percentage
              />
              <InputSmall
                value={dd.start}
                onChange={(val) => {
                  const updated = [...drawdowns];
                  updated[i].start = val;
                  setDrawdowns(updated);
                }}
                // isError={inputErrors.start}
              />
              <InputSmall
                value={dd.end}
                onChange={(val) => {
                  const updated = [...drawdowns];
                  updated[i].end = val;
                  setDrawdowns(updated);
                }}
                // isError={inputErrors.end}
              />

              <RemoveIcon onClick={() => handleRemoveDrawdown(i)} />
            </div>
          ))}
        </div>

        <div className="add-button">
          <AddButton
            onClick={() => {
              // handleAddDrawdown();
              handleAdd();
            }}
            isDisabled={!isPensionValid || !areDrawdownsValid}
            // isDisabled={false}
          />
        </div>
      </div>

      {/* Pension Inputs from global state */}
      {pensions.map((pension, index) => (
        <React.Fragment key={index}>
          <div className="card-divider" />
          <div className="dialog-row">
            <div className="drawdown-column-container">
              <div className="dialog-row-inputs">
                <Input2Medium
                  value={pension.name}
                  onChange={(val) => {
                    const updatedPensions = [...pensions];
                    updatedPensions[index].name = val;
                    dispatch({
                      type: "UPDATE_PENSIONS",
                      payload: updatedPensions,
                    });
                  }}
                  type="invType"
                  options={nameOptions}
                  readOnly
                />
                <Input1Big
                  value={tempTypes[index] ?? ""}
                  onChange={(val) => {
                    // Update local UI value
                    const updatedNames = [...tempTypes];
                    updatedNames[index] = val;
                    setTempTypes(updatedNames);

                    // Only update global state if it's not blank
                    if (val.trim() !== "") {
                      const updatedPensions = [...pensions];
                      updatedPensions[index].type = val;
                      dispatch({
                        type: "UPDATE_PENSIONS",
                        payload: updatedPensions,
                      });
                    }
                  }}
                  onBlur={() => {
                    const currentVal = tempTypes[index]?.trim();

                    if (currentVal === "") {
                      // Restore last valid pension name from state
                      const updatedNames = [...tempTypes];
                      updatedNames[index] = pensions[index].type;
                      setTempTypes(updatedNames);
                    }
                  }}
                  readOnly
                />

                <Input1Big
                  value={pension.fundValue.value}
                  onChange={(val) =>
                    handlePensionFieldChange(index, "fundValue", val, "value")
                  }
                  onUnitChange={(unit) =>
                    handlePensionFieldChange(index, "fundValue", unit, "unit")
                  }
                  type="number"
                  currency={true} // Assuming fund value is in currency
                />
                <Input2Medium
                  value={pension.contribution.value}
                  onChange={(val) =>
                    handlePensionFieldChange(
                      index,
                      "contribution",
                      val,
                      "value"
                    )
                  }
                  onUnitChange={(unit) =>
                    handlePensionFieldChange(
                      index,
                      "contribution",
                      unit,
                      "unit"
                    )
                  }
                  currentUnit={pension.contribution?.unit}
                  options={TimelineOptions}
                />

                <Input2Medium
                  value={pension.atr ? `${pension.atr}%` : ""}
                  type="per"
                  onChange={(val) => {
                    // const newVal = val.trim() === "" ? "" : getATRValue(val.label);
                    const updatedPensions = [...pensions];
                    updatedPensions[index].atr = val;
                    dispatch({
                      type: "UPDATE_PENSIONS",
                      payload: updatedPensions,
                    });
                  }}
                  options={ATROptions}
                  per={true}
                  // readOnly={true} // Assuming ATR is read-only
                />

                {/* {pensionDrawdownErrors[index]?.rangeError && (
                <div className="error Label right-align">
                  Drawdown range overlaps with an existing entry.
                </div>
              )} */}
              </div>
              {pensionDrawdownErrors[index]?.rangeError && (
                <div className="error Label right-align">
                  Drawdown range overlaps with an existing entry.
                </div>
              )}
            </div>

            <div className="drawdown-container">
              {/* Local live input row */}

              <div className="card-rows no-label-container drawdown">
                <Input2Medium
                  value={pensionDrawdownInputs[index]?.value || ""}
                  onChange={(val) => {
                    handlePensionDrawdownInputChange(index, "value", val);
                  }}
                  onUnitChange={(unit) =>
                    handlePensionDrawdownInputChange(index, "unit", unit)
                  }
                  options={DrawdownOptions}
                  isError={pensionDrawdownErrors[index]?.value}
                  type="number"
                  currency={
                    pensionDrawdownInputs[index]?.unit === "Per annum" ||
                    pensionDrawdownInputs[index]?.unit === "Annual"
                  } // Assuming drawdown value is in currency
                  per={pensionDrawdownInputs[index]?.unit === "Percentage %"} // Assuming drawdown value can be percentage
                />
                <InputSmall
                  value={pensionDrawdownInputs[index]?.start || ""}
                  onChange={(val) => {
                    handlePensionDrawdownInputChange(index, "start", val);
                    const start = parseInt(val, 10);
                    const end = parseInt(
                      pensionDrawdownInputs[index]?.end || ""
                    );
                    if (!isNaN(start) && !isNaN(end) && end < start) {
                      setPensionDrawdownErrors((prev) => ({
                        ...prev,
                        [index]: {
                          ...prev[index],
                          end: true,
                        },
                      }));
                    } else {
                      setPensionDrawdownErrors((prev) => ({
                        ...prev,
                        [index]: {
                          ...prev[index],
                          end: false,
                        },
                      }));
                    }
                  }}
                  isError={pensionDrawdownErrors[index]?.start}
                />
                <InputSmall
                  value={pensionDrawdownInputs[index]?.end || ""}
                  onChange={(val) => {
                    handlePensionDrawdownInputChange(index, "end", val);
                    const end = parseInt(val, 10);
                    const start = parseInt(
                      pensionDrawdownInputs[index]?.start || ""
                    );
                    if (!isNaN(start) && !isNaN(end) && end < start) {
                      setPensionDrawdownErrors((prev) => ({
                        ...prev,
                        [index]: {
                          ...prev[index],
                          end: true,
                        },
                      }));
                    } else {
                      setPensionDrawdownErrors((prev) => ({
                        ...prev,
                        [index]: {
                          ...prev[index],
                          end: false,
                        },
                      }));
                    }
                  }}
                  isError={pensionDrawdownErrors[index]?.end}
                />
                <AddIcon onClick={() => handleAddPensionDrawdown(index)} />
              </div>
              {/* {pensionDrawdownErrors[index]?.end && (
                <div className="error Label">Age must be 18 or older</div>
              )} */}

              {/* Added pension drawdowns */}
              {(pension.drawdowns || []).map((dd, ddIndex) => (
                <div
                  key={ddIndex}
                  className="card-rows no-label-container drawdown"
                >
                  <Input2Medium
                    value={dd.value}
                    onChange={(val) => {
                      if (val.trim() !== "") {
                        const updated = [...pensions];
                        updated[index].drawdowns[ddIndex].value = val;
                        dispatch({
                          type: "UPDATE_PENSIONS",
                          payload: updated,
                        });
                      }
                    }}
                    onUnitChange={(unit) => {
                      const updated = [...pensions];
                      updated[index].drawdowns[ddIndex].unit = unit;
                      dispatch({ type: "UPDATE_PENSIONS", payload: updated });
                    }}
                    options={DrawdownOptions}
                    currency={dd.unit === "Per annum" || dd.unit === "Annual"} // Assuming drawdown value is in currency
                    per={dd.unit === "Percentage %"} // Assuming drawdown value can be percentage
                    // readOnly
                  />
                  <InputSmall
                    value={dd.start}
                    onChange={(val) => {
                      if (val.trim() !== "") {
                        const updated = [...pensions];
                        updated[index].drawdowns[ddIndex].start = val;
                        dispatch({ type: "UPDATE_PENSIONS", payload: updated });
                      }
                    }}
                    // readOnly={true}
                  />
                  <InputSmall
                    value={dd.end}
                    isError={Number(dd.end) < Number(dd.start)}
                    onChange={(val) => {
                      const updated = [...pensions];
                      updated[index].drawdowns[ddIndex].end = val;

                      // prevent update if end is less than start
                      if (
                        Number(val) <
                        Number(updated[index].drawdowns[ddIndex].start)
                      ) {
                        setPensionDrawdownErrors((prev) => ({
                          ...prev,
                          [index]: {
                            ...(prev[index] || {}),
                            [`${ddIndex}-end`]: true,
                          },
                        }));
                        return;
                      }

                      // clear error if valid
                      setPensionDrawdownErrors((prev) => ({
                        ...prev,
                        [index]: {
                          ...(prev[index] || {}),
                          [`${ddIndex}-end`]: false,
                        },
                      }));

                      dispatch({ type: "UPDATE_PENSIONS", payload: updated });
                    }}
                    // readOnly={true}
                  />

                  <RemoveIcon
                    onClick={() => {
                      const updated = [...pensions];
                      updated[index].drawdowns = updated[
                        index
                      ].drawdowns.filter((_, i) => i !== ddIndex);
                      dispatch({ type: "UPDATE_PENSIONS", payload: updated });
                    }}
                  />
                </div>
              ))}
            </div>

            <RemoveButton onClick={() => handleRemovePension(index)} />
            {pensionDrawdownErrors.rangeError && (
              <div className="error Label">
                Drawdown range overlaps with an existing entry.
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default PensionDialogBox;
